// @pages/workspace/DocumentCreation.page.tsx

import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  renderRoute,
  routeDocumentDetailsPrompt,
  routeDocumentSelection,
} from "@navigation/typedRoutes";
import type { Route } from "@navigation/typedRoutes";
import DocumentCreationComponent from "@view/workspace/DocumentCreation";
import { useLifecycleClient } from "@contexts/LifecycleClient.context";
import { useDocumentListContext } from "@contexts/DocumentList.context";
import { useDocumentContext } from "@contexts/Document.context";
import { useLoadingController } from "@contexts/Loading.context";
import type { DocumentCreationKernel } from "@ps-bridge/contracts/documentCreation";
import { buildDocumentCreationViewProps } from "@ps-bridge/documentCreation.program";
import { ok, type Ok } from "@ps-bridge/ok";
import type { IOts, TaskTs } from "@ps-bridge/interop";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import { ioEffect, ioTap } from "@utils/effects/branded";
import { useRunTaskEither, useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { useOnInit } from "@hooks/effects/useIoEffect";
import type { DocumentMetadata, DocumentWizardType } from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import { makeDocumentWizardType } from "@ps-bridge/models/brands/documentWizardType";
import { makeDocumentOutline } from "@ps-bridge/models/brands/documentOutline";
import { makeDocumentStyle } from "@ps-bridge/models/brands/documentStyle";
import { makeDocumentName } from "@ps-bridge/models/brands/documentName";
import { makeDocumentUploadFile } from "@ps-bridge/models/brands/documentUploadFile";
import { makeDocumentUploadFileName } from "@ps-bridge/models/brands/documentUploadFileName";
import { useIoOptionState } from "@utils/useIoOptionState";
import { makeMimeType } from "@ps-bridge/models/brands/mimeType";
import * as Option from "@utils/Option";
import type {
  DocumentCreationBackPhase,
  DocumentCreationStep,
  DocumentCreationStepPhase,
} from "@view/panels/workspace/document-creation/DocumentCreation.panel";

type NextAction = {
  action: IOts<Ok>;
  label: string;
};

const steps: DocumentCreationStep[] = [
  {
    id: "details",
    title: "Define",
    caption: "Name, intentions, outline",
  },
  {
    id: "review",
    title: "Review",
    caption: "Confirm before creation",
  },
];

const maxStepIndex = 1;
const documentType: DocumentWizardType = makeDocumentWizardType("scratch");
const sourceUploadAccept = [".md", ".docx"];

const fileToBytes = (file: File): Promise<Uint8Array> => {
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Unable to read source document."));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(file);
  });
};

const fileToBase64 = async (file: File): Promise<string> => {
  const bytes = await fileToBytes(file);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return window.btoa(binary);
};

const isSupportedSourceUpload = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return sourceUploadAccept.some((extension) => fileName.endsWith(extension));
};

const DocumentCreationPage: React.FC = () => {
  const navigate = useNavigate();
  const lifecycleClient = useLifecycleClient();
  const { availableDocuments: availableDocumentsHandle } = useDocumentListContext();
  const { document: documentHandle } = useDocumentContext();
  const loadingController = useLoadingController();
  const runTaskEither = useRunTaskEither();
  const runTaskWithError = useRunTaskWithError();
  const { read: title, setSome: setTitle } = useIoOptionState<string>(Option.none());
  const { read: styleText, setSome: setStyleText } = useIoOptionState<string>(Option.none());
  const { read: outline, setSome: setOutline } = useIoOptionState<string>(Option.none());
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedSourceDocumentIds, setSelectedSourceDocumentIds] = useState<DocumentID[]>([]);
  const [isSourceDocumentUploading, setIsSourceDocumentUploading] = useState(false);
  const [sourceDocumentUploadError, setSourceDocumentUploadError] = useState("");

  const navigateToRoute = useCallback(
    (route: Route): IOts<Ok> =>
      ioEffect(() => {
        navigate(renderRoute(route));
        return ok;
      }),
    [navigate],
  );

  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );

  const kernel: DocumentCreationKernel = useMemo(
    () => ({
      loading: loadingController,
      lifecycle: lifecycleClient,
      document: documentHandle,
      availableDocuments: availableDocumentsHandle,
      navigate: mkNavigate(navigateToRoute),
      documentDetailsPromptRoute: mkRouteBuilder(routeDocumentDetailsPrompt),
      taskRunner: mkTaskRunner(runTaskWithErrorIo),
    }),
    [
      loadingController,
      lifecycleClient,
      documentHandle,
      availableDocumentsHandle,
      navigateToRoute,
      runTaskWithErrorIo,
    ],
  );

  const viewProps = useMemo(
    () =>
      buildDocumentCreationViewProps({
        kernel,
        documentSelectionRoute: routeDocumentSelection,
      }),
    [kernel],
  );

  useOnInit(viewProps.onInit);

  const selectedSourceDocuments = useMemo(
    () =>
      selectedSourceDocumentIds
        .map((id) => viewProps.availableDocuments.find((document) => document.id === id))
        .filter((document): document is DocumentMetadata => document !== undefined),
    [selectedSourceDocumentIds, viewProps.availableDocuments],
  );

  const advanceStep: IOts<Ok> = useMemo(
    () =>
      ioEffect(() => {
        setStepIndex((current) => Math.min(maxStepIndex, current + 1));
        return ok;
      }),
    [],
  );

  const retreatStep: IOts<Ok> = useMemo(
    () =>
      ioEffect(() => {
        setStepIndex((current) => Math.max(0, current - 1));
        return ok;
      }),
    [],
  );

  const titleValue = Option.fold(title, () => "", (value) => value);
  const styleValue = Option.fold(styleText, () => "", (value) => value);
  const outlineValue = Option.fold(outline, () => "", (value) => value);
  const outlineLines = useMemo(
    () =>
      outlineValue
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    [outlineValue],
  );
  const cleanOutline = useMemo(() => outlineLines.join("\n"), [outlineLines]);
  const cleanStyle = styleValue.trim().length > 0 ? styleValue.trim() : "Neutral tone.";
  const canContinue = titleValue.trim().length > 0 && outlineLines.length > 0;
  const stepPhase: DocumentCreationStepPhase = stepIndex === 0 ? "details" : "review";
  const backPhase: DocumentCreationBackPhase = stepIndex > 0 ? "visible" : "hidden";
  const nextDisabled = stepIndex === 0 && !canContinue;
  const nextAction: NextAction =
    stepIndex < maxStepIndex
      ? {
          action: advanceStep,
          label: "Continue",
        }
      : {
          action: viewProps.onCreateDocument({
            type: documentType,
            name: makeDocumentName(titleValue.trim()),
            style: makeDocumentStyle(cleanStyle),
            outline: makeDocumentOutline(cleanOutline),
            source_document_ids: selectedSourceDocuments.map((document) => document.id),
          }),
          label: "Create Document",
        };

  const onToggleSourceDocument = useCallback((documentId: DocumentID): Ok => {
    setSelectedSourceDocumentIds((current) => {
      if (current.includes(documentId)) {
        return current.filter((id) => id !== documentId);
      }
      if (current.length >= 2) {
        return current;
      }
      return [...current, documentId];
    });
    return ok;
  }, []);

  const onUploadSourceDocument = useCallback(
    (file: File): Ok => {
      if (!isSupportedSourceUpload(file)) {
        setSourceDocumentUploadError("Upload a .md or .docx source document.");
        return ok;
      }
      setIsSourceDocumentUploading(true);
      setSourceDocumentUploadError("");
      fileToBase64(file).then((contentsBase64) =>
        runTaskEither(
          lifecycleClient.uploadSourceDocument({
            file_name: makeDocumentUploadFileName(file.name),
            content_type: makeMimeType(file.type || "application/octet-stream"),
            contents_base64: makeDocumentUploadFile(contentsBase64),
          }),
          {
            onLeft: (error) =>
              ioEffect(() => {
                setSourceDocumentUploadError(error.message);
                setIsSourceDocumentUploading(false);
                return ok;
              }),
            onRight: (response) =>
              {
                const uploadedDocument = response.document;
                const nextDocuments = [
                  uploadedDocument,
                  ...availableDocumentsHandle.read.filter((document) => document.id !== uploadedDocument.id),
                ];
                return ioTap(availableDocumentsHandle.set(nextDocuments), () =>
                  ioEffect(() => {
                    setSelectedSourceDocumentIds((current) => {
                      if (current.includes(uploadedDocument.id) || current.length >= 2) {
                        return current;
                      }
                      return [...current, uploadedDocument.id];
                    });
                    setIsSourceDocumentUploading(false);
                    return ok;
                  }),
                );
              },
          },
        ),
      ).catch((error) => {
        const message = error instanceof Error ? error.message : "Unable to upload source document.";
        setSourceDocumentUploadError(message);
        setIsSourceDocumentUploading(false);
      });
      return ok;
    },
    [availableDocumentsHandle, lifecycleClient, runTaskEither],
  );

  return (
    <DocumentCreationComponent
      {...viewProps}
      titleValue={titleValue}
      styleValue={styleValue}
      outlineValue={outlineValue}
      outlineLines={outlineLines}
      onTitleChange={setTitle}
      onStyleChange={setStyleText}
      onOutlineChange={setOutline}
      stepIndex={stepIndex}
      stepPhase={stepPhase}
      backPhase={backPhase}
      steps={steps}
      nextActionLabel={nextAction.label}
      nextAction={nextAction.action}
      nextDisabled={nextDisabled}
      onBack={retreatStep}
      selectedSourceDocumentIds={selectedSourceDocumentIds}
      selectedSourceDocuments={selectedSourceDocuments}
      onToggleSourceDocument={onToggleSourceDocument}
      onUploadSourceDocument={onUploadSourceDocument}
      isSourceDocumentUploading={isSourceDocumentUploading}
      sourceDocumentUploadError={sourceDocumentUploadError}
    />
  );
};

export default DocumentCreationPage;
